import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Avatar, Button, CircularProgress, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';
import { BadgeOutlined, HistoryRounded, LogoutRounded } from '@mui/icons-material';
import { accountSelector, setNaming } from '../services/AccountSlice';
import { signIn, signOut } from './session';
import './account.css';

// Google's "G", drawn here rather than loaded from anywhere.
export const GoogleMark = () => (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.2l7.8 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.5 5.8c4.4-4 6.8-10 6.8-17.2z" />
        <path fill="#FBBC05" d="M10.5 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
        <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
    </svg>
);

/** Sign in, or once signed in, the player's avatar with their menu. */
const AccountButton = () => {
    const dispatch = useDispatch();
    const { status, profile, busy } = useSelector(accountSelector);
    const [anchor, setAnchor] = useState(null);

    if (status === 'unknown') {
        return null;
    }
    if (status !== 'signedIn') {
        return (
            <Tooltip title="Sign in with Google" describeChild>
                <Button
                    className="account-signin"
                    variant="outlined"
                    onClick={() => !busy && signIn(dispatch)}
                    aria-busy={busy}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <GoogleMark />}
                >
                    {/* On a narrow game bar only the G shows; the words stay for screen readers. */}
                    <span className="account-signin-label">{busy ? 'Signing in…' : 'Sign in'}</span>
                </Button>
            </Tooltip>
        );
    }
    const initial = (profile.displayName || '?').charAt(0).toUpperCase();
    return (
        <>
            <Tooltip title={profile.displayName}>
                <IconButton className="account-avatar" onClick={(event) => setAnchor(event.currentTarget)} aria-label="Your account">
                    <Avatar src={profile.picture || undefined} alt="" imgProps={{ referrerPolicy: 'no-referrer' }}>{initial}</Avatar>
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <div className="account-menu-head">
                    <strong>{profile.displayName}</strong>
                    {profile.email && <span>{profile.email}</span>}
                </div>
                <Divider />
                <MenuItem component={Link} to="/my-games" onClick={() => setAnchor(null)}>
                    <ListItemIcon><HistoryRounded fontSize="small" /></ListItemIcon>My games
                </MenuItem>
                <MenuItem onClick={() => { setAnchor(null); dispatch(setNaming(true)); }}>
                    <ListItemIcon><BadgeOutlined fontSize="small" /></ListItemIcon>{profile.name ? 'Change leaderboard name' : 'Choose leaderboard name'}
                </MenuItem>
                <MenuItem onClick={() => { setAnchor(null); signOut(dispatch); }}>
                    <ListItemIcon><LogoutRounded fontSize="small" /></ListItemIcon>Sign out
                </MenuItem>
            </Menu>
        </>
    );
};

export default AccountButton;
